/**
 * DLP の設定 `/dlp/settings`(dlp:admin)。core settings の `dlp` キーをフォームで触る(docs/dlp.md §7)。
 * 保存すると全端末の config_version が +1 され、次の poll で届く。
 */
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Card, Form, InputNumber, Select, Space, Switch, Typography, message } from "antd";
import { useEffect } from "react";

import type { DlpSettings } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { PageHeader } from "../../ui";
import { MailSettingsFields } from "./MailSettingsFields";
import { filesSettingsError } from "./mailFiles";
import { USB_MODES } from "./constants";
import { errorText, useDlpPerms, useDlpSettings, useInvalidateDlp } from "./hooks";

const { Text } = Typography;

export function SettingsPage() {
  const api = useApi();
  const { canAdmin, hasExitScan, hasUsb, hasUpload, readOnly } = useDlpPerms();
  const invalidate = useInvalidateDlp();
  const query = useDlpSettings();
  const [form] = Form.useForm<DlpSettings>();

  useEffect(() => {
    if (query.data) form.setFieldsValue(query.data);
  }, [query.data, form]);

  const save = useMutation({
    mutationFn: (v: DlpSettings) => api.dlpSaveSettings(v),
    onSuccess: () => {
      void message.success("保存した(監査ログに記録した。端末には次の poll で届く)");
      invalidate();
    },
    onError: (e) => void message.error(filesSettingsError(e) ?? errorText(e), 8),
  });

  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="設定"
          description="エージェントの間隔、USB の既定、アップロード検知。保存すると全端末の設定版が上がり、次の poll で反映される。"
        />
        {query.isError && (
          <Alert type="error" showIcon style={{ marginBottom: 12 }} message="設定を取得できなかった" description={errorText(query.error)} />
        )}
        <Form<DlpSettings>
          form={form}
          layout="vertical"
          onFinish={(v) => save.mutate(v)}
          disabled={!canAdmin || readOnly || query.isLoading || save.isPending}
          style={{ maxWidth: 640 }}
        >
          <Card size="small" title="退職時スキャン" style={{ marginBottom: 16 }}>
            {!hasExitScan && <Alert type="info" showIcon style={{ marginBottom: 12 }} message="退職時スキャンは契約に無い(設定は保存できる)" />}
            <Form.Item
              name="offboard_auto_scan"
              label="退職の申請が完了したら自動でスキャンを作る"
              valuePropName="checked"
              extra="従業員に端末が 1 台も付いていなければ「対象なし」の通知だけ出す(後から端末が付いても自動では作らない)"
            >
              <Switch />
            </Form.Item>
          </Card>

          <Card size="small" title="USB" style={{ marginBottom: 16 }}>
            {!hasUsb && <Alert type="info" showIcon style={{ marginBottom: 12 }} message="USB 制御は契約に無い(設定は保存できる)" />}
            <Form.Item name="usb_log" label="着脱を記録する" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Space size={16} wrap align="start">
              <Form.Item name="usb_poll_sec" label="ドライブを見る間隔(秒)" rules={[{ required: true }]} extra="5〜300">
                <InputNumber min={5} max={300} />
              </Form.Item>
              <Form.Item
                name="usb_mode_default"
                label="既定の USB モード"
                rules={[{ required: true }]}
                extra="端末ごとに上書きできる(端末の右パネル)"
              >
                <Select style={{ width: 200 }} options={USB_MODES} />
              </Form.Item>
            </Space>
          </Card>

          <Card size="small" title="アップロード検知" style={{ marginBottom: 16 }}>
            {!hasUpload && <Alert type="info" showIcon style={{ marginBottom: 12 }} message="アップロード検知は契約に無い(設定は保存できる)" />}
            <Form.Item
              name="upload_monitor"
              label="観測する"
              valuePropName="checked"
              extra="一覧に載った送信先への送信量を端末で推定して記録する。送った中身・ファイル名は分からない(TLS を復号しない)。切ると端末は観測を止め、自分が書いたブラウザのポリシーを消す"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="upload_block"
              label="ブラウザで遮断する"
              valuePropName="checked"
              extra="ブラウザのポリシー(Chrome / Edge の URLBlocklist、Firefox の WebsiteFilter)を書き換え、送信先の一覧で「遮断」にしたドメインを開けなくする。同期アプリ・コマンドラインは止まらない。他の管理者が別の値を入れているブラウザには書かない(端末の右パネルで「競合」と出る)"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="upload_disable_doh"
              label="ブラウザの DoH を切る"
              valuePropName="checked"
              extra="ブラウザ内蔵の DNS over HTTPS を使うと宛先の名前が取れず、一覧に当たらなくなる。ポリシーで切って OS の DNS を使わせる"
            >
              <Switch />
            </Form.Item>
            <Space size={16} wrap align="start">
              <Form.Item
                name="upload_window_sec"
                label="集計の窓(秒)"
                rules={[
                  { required: true },
                  { validator: (_, v) => (v == null || v % 60 === 0 ? Promise.resolve() : Promise.reject(new Error("60 の倍数にする"))) },
                ]}
                extra="60〜3600、60 の倍数。この単位で送信量をまとめて 1 件にする"
              >
                <InputNumber min={60} max={3600} step={60} />
              </Form.Item>
              <Form.Item
                name="upload_min_kib"
                label="検知の閾値(KiB)"
                rules={[{ required: true }]}
                extra="64〜1048576。一覧の送信先で、窓あたりこれ未満は捨てる(1024 = 1MiB)"
              >
                <InputNumber min={64} max={1048576} step={64} />
              </Form.Item>
              <Form.Item
                name="upload_unknown_mib"
                label="宛先不明の大量送信(MiB)"
                rules={[{ required: true }]}
                extra="0〜100000(0 = 記録しない)。一覧に当たらない送信が窓あたりこれ以上なら「宛先不明の大量送信」として残す"
              >
                <InputNumber min={0} max={100000} />
              </Form.Item>
            </Space>
          </Card>

          <MailSettingsFields />

          <Card size="small" title="エージェントの間隔" style={{ marginBottom: 16 }}>
            <Space size={16} wrap align="start">
              <Form.Item name="poll_sec" label="poll(秒)" rules={[{ required: true }]} extra="60〜3600。スキャンの依頼はこの間隔で端末に渡る">
                <InputNumber min={60} max={3600} step={60} />
              </Form.Item>
              <Form.Item name="report_sec" label="report(秒)" rules={[{ required: true }]} extra="30〜600。USB イベントと走査の結果はこの間隔で届く">
                <InputNumber min={30} max={600} step={30} />
              </Form.Item>
            </Space>
          </Card>

          <Space>
            <Button type="primary" htmlType="submit" loading={save.isPending} disabled={!canAdmin || readOnly}>
              保存
            </Button>
            {!canAdmin && <Text type="secondary">変更は dlp:admin</Text>}
          </Space>
        </Form>
      </div>
    </div>
  );
}
